import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { initialsFrom, pickForeground } from "@/lib/brand";
import { formatCurrency, formatDate } from "@/lib/format";
import { amountDueNow, type PublicInvoiceResponse } from "@/api/public";
import dolfinLogo from "../logo-light.svg";

// Card with a 4px brand-coloured top accent bar. The bar is rendered as
// the first child so `overflow-hidden` clips it to the rounded corners.
export function BrandedCard({
  brand,
  children,
}: {
  brand: string;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <div aria-hidden className="h-1" style={{ backgroundColor: brand }} />
      {children}
    </Card>
  );
}

export function LogoOrMonogram({
  invoice,
  brand,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
}) {
  if (invoice.organisationLogoUrl) {
    return (
      <img
        src={invoice.organisationLogoUrl}
        alt={invoice.organisationName}
        className="h-12 w-auto max-w-[160px] object-contain"
      />
    );
  }
  return (
    <div
      aria-hidden
      className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold"
      style={{ backgroundColor: brand, color: pickForeground(brand) }}
    >
      {initialsFrom(invoice.organisationName)}
    </div>
  );
}

export function InvoiceCard({
  invoice,
  brand,
  amountTint,
  children,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
  amountTint?: string;
  children: ReactNode;
}) {
  const dueDate = formatDate(invoice.dueDate);

  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        <LogoOrMonogram invoice={invoice} brand={brand} />
        <CardTitle className="text-lg font-semibold">{invoice.organisationName}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <div
            className="text-4xl font-semibold tracking-tight sm:text-5xl"
            style={amountTint ? { color: amountTint } : undefined}
          >
            {formatCurrency(amountDueNow(invoice), invoice.currency)}
          </div>
          {invoice.earlyPaymentDiscount ? (
            <div className="mt-2 text-sm font-medium" data-testid="early-payment-discount">
              Pay {formatCurrency(invoice.earlyPaymentDiscount.amountDueNow, invoice.currency)} by{" "}
              {/* A calendar day: read at local midnight so no time zone moves it. */}
              {formatDate(`${invoice.earlyPaymentDiscount.expiresOn}T00:00:00`)} - save{" "}
              {formatCurrency(invoice.earlyPaymentDiscount.amount, invoice.currency)}
              <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                {formatCurrency(invoice.amountDue, invoice.currency)} after that
              </div>
            </div>
          ) : null}
          <div className="mt-1 text-sm text-muted-foreground">
            Invoice {invoice.documentNumber}
          </div>
          {invoice.amountCredited > 0 ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {formatCurrency(invoice.totalAmount, invoice.currency)} total ·{" "}
              {formatCurrency(invoice.amountCredited, invoice.currency)} credit applied
            </div>
          ) : null}
        </div>

        <div className="space-y-1 text-sm text-muted-foreground">
          <div>Billed to {invoice.customerName}</div>
          {dueDate ? <div>Due {dueDate}</div> : null}
          {invoice.memo ? <div className="truncate italic">{invoice.memo}</div> : null}
        </div>

        {children}
      </CardContent>
    </BrandedCard>
  );
}

// `note` names the payment provider; null hides it (the bill pay page pays by bank, not through Stripe).
export function Footer({ note = "Secured by Stripe" }: { note?: string | null }) {
  return (
    <div className="mt-6 flex flex-col items-center gap-1 text-xs text-muted-foreground">
      {note ? <div>{note}</div> : null}
      <a
        href="https://dolfinai.co"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 opacity-60 hover:opacity-100"
      >
        <img src={dolfinLogo} alt="" className="h-3 w-3" />
        <span>Dolfin</span>
      </a>
    </div>
  );
}
